// Header filter popover for the object-driven sidebar. Renders one or more
// checkbox groups (Calendar type + status) and writes the selection to the URL
// as comma-joined params — the pattern every filtered panel uses. Selection is
// read back OUT of the URL, so a deep link, the panel, and the page can never
// disagree.

import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Check, SlidersHorizontal } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger, cn } from '@clarix/ui';
import type { FilterPopoverConfig } from '@clarix/shared-types';
import { dotToneClass } from './tones';

type Group = FilterPopoverConfig['groups'][number];

interface Props {
  config: FilterPopoverConfig;
  pathname: string;
}

export function SidebarFilterPopover({ config, pathname }: Props) {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [open, setOpen] = useState(false);

  // Current selection per group: the URL param if present, else the default.
  const selectionFor = (g: Group): string[] => {
    const raw = params.get(g.param);
    if (raw != null) return raw.split(',').filter(Boolean);
    return g.defaultAll ? g.options.map((o) => o.id) : [];
  };

  const isGroupActive = (g: Group) => {
    const sel = selectionFor(g);
    return g.defaultAll ? sel.length < g.options.length : sel.length > 0;
  };
  const hasActiveFilters = config.groups.some(isGroupActive);

  const writeUrl = (next: Record<string, string[]>) => {
    const qp = new URLSearchParams();
    for (const key of config.preserveParams ?? []) {
      const v = params.get(key);
      if (v != null) qp.set(key, v);
    }
    for (const g of config.groups) {
      qp.set(g.param, (next[g.param] ?? selectionFor(g)).join(','));
    }
    navigate(`${pathname}?${qp.toString()}`);
  };

  const toggleOption = (g: Group, id: string) => {
    const cur = selectionFor(g);
    const next = cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id];
    writeUrl({ [g.param]: next });
  };

  const clearAll = () => {
    const next: Record<string, string[]> = {};
    for (const g of config.groups) next[g.param] = g.defaultAll ? g.options.map((o) => o.id) : [];
    writeUrl(next);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={config.ariaLabel}
          className={cn(
            'relative flex h-5 w-5 flex-shrink-0 items-center justify-center rounded transition-colors',
            hasActiveFilters
              ? 'text-indigo-600 hover:bg-indigo-50'
              : 'text-gray-400 hover:bg-gray-100 hover:text-gray-600',
          )}
        >
          <SlidersHorizontal className="h-3 w-3" strokeWidth={1.5} />
          {hasActiveFilters && (
            <span className="absolute -right-0.5 -top-0.5 h-1.5 w-1.5 rounded-full bg-indigo-500" />
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent side="right" align="start" sideOffset={8} className="w-48 p-0">
        <div className="py-1.5">
          {config.groups.map((g, gi) => {
            const sel = selectionFor(g);
            return (
              <div key={g.key}>
                {gi > 0 && <div className="mx-3 my-1.5 border-t border-gray-100" />}
                <div className="text-muted-foreground px-3 pb-1.5 pt-1 text-xs font-semibold uppercase tracking-wider">
                  {g.label}
                </div>
                {g.options.map((o) => {
                  const selected = sel.includes(o.id);
                  return (
                    <button
                      key={o.id}
                      type="button"
                      onClick={() => toggleOption(g, o.id)}
                      className={cn(
                        'flex w-full items-center px-3 py-1.5 text-lg transition-colors',
                        selected
                          ? 'bg-indigo-50/70 text-indigo-700'
                          : 'text-foreground hover:bg-gray-50',
                      )}
                    >
                      <span
                        className={cn(
                          'mr-2.5 flex h-3.5 w-3.5 flex-shrink-0 items-center justify-center rounded border transition-colors',
                          selected ? 'border-indigo-500 bg-indigo-500' : 'border-gray-300 bg-white',
                        )}
                      >
                        {selected && <Check className="h-2.5 w-2.5 text-white" strokeWidth={3} />}
                      </span>
                      {o.tone && (
                        <span
                          className={cn(
                            'mr-1.5 h-2 w-2 flex-shrink-0 rounded-full',
                            dotToneClass(o.tone),
                          )}
                        />
                      )}
                      <span className="flex-1 text-left">{o.label}</span>
                    </button>
                  );
                })}
              </div>
            );
          })}
          {hasActiveFilters && (
            <>
              <div className="mx-3 my-1.5 border-t border-gray-100" />
              <button
                type="button"
                onClick={clearAll}
                className="w-full px-3 py-1.5 text-left text-lg text-indigo-600 transition-colors hover:bg-indigo-50"
              >
                Clear all filters
              </button>
            </>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
